<template>
  <div :class="`page ${loading ? 'loading' : ''}`">
    <div class="card" v-if="loaded && product">
      <!-- LOGО -->
      <img class="logo" src="@/assets/logo.svg" alt="Glamour Avenue" />

      <div class="image-wrap" v-if="imageUrl">
        <img
          :src="imageUrl"
          :alt="product.name || 'Product image'"
          class="image"
        />
      </div>

      <h1 class="title">{{ product.name }}</h1>
      <p v-if="product.brand" class="brand">{{ product.brand }}</p>

      <!-- Цена отображается по выбранному варианту, иначе берём product.price -->
      <div v-if="displayPrice !== null && displayPrice > 0" class="price">
        {{ formatPrice(displayPrice) }} сум
      </div>

      <!-- Описание -->
      <p v-if="product.description" class="desc">
        {{ product.description }}
      </p>

      <p v-if="product.detail" class="detail">
        {{ product.detail }}
      </p>

      <div v-if="product.categories && product.categories.length" class="cats">
        <span class="chip" v-for="c in product.categories" :key="c.id">{{
          c.name
        }}</span>
      </div>

      <!-- Варианты (размер/цена) -->
      <div v-if="variants && variants.length" class="variants">
        <div class="variants-title">Выберите размер</div>
        <div class="variants-list">
          <button
            v-for="(v, i) in variants"
            :key="v.sku || i"
            type="button"
            class="variant-chip"
            :class="{ active: i === selectedVariantIndex }"
            @click="selectVariant(i)"
          >
            <div class="variant-size">{{ v.size || "—" }}</div>
          </button>
        </div>
      </div>

      <!-- Форма заказа -->
      <form class="order-form" @submit.prevent="submitForm" v-if="!sent">
        <input
          type="number"
          v-model="form.phone"
          placeholder="Телефон"
          maxlength="12"
          required
        />
        <input
          type="text"
          v-model="form.name"
          placeholder="Ваше имя"
          required
        />
        <!-- <textarea v-model="form.comment" rows="3" placeholder="Комментарий"></textarea> -->

        <!-- скрытые поля для бэкенда -->
        <input type="hidden" name="size" :value="form.size" />
        <input type="hidden" name="sku" :value="form.sku" />
        <input type="hidden" name="price" :value="form.price" />

        <button type="submit">Купить в 1 клик</button>
      </form>
    </div>

    <div v-else-if="loading" class="state">Загрузка…</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <div v-else class="state">Нет данных по товару</div>
  </div>
</template>

<script>
export default {
  name: "Search",
  data() {
    return {
      sent: false, // форма доступна
      product: null,
      loading: false,
      loaded: false,
      error: null,
      cdnBase: "https://files.glamourboutique.uz/products/",
      form: {
        phone: "",
        name: "",
        comment: "",
        size: "",
        sku: "",
        price: "", // храним строкой/числом — как приходит, отправляем как есть
      },
      selectedVariantIndex: 0,
    };
  },
  computed: {
    variants() {
      return this.product && Array.isArray(this.product.variants)
        ? this.product.variants
        : [];
    },
    selectedVariant() {
      if (!this.variants.length) return null;
      const idx = Math.min(this.selectedVariantIndex, this.variants.length - 1);
      return this.variants[idx] || null;
    },
    // Картинка: сначала — из выбранного варианта, затем — из product.images
    imageUrl() {
      // 1) у варианта может быть абсолютная ссылка (image), берём её
      if (this.selectedVariant && this.selectedVariant.image) {
        return this.selectedVariant.image;
      }
      // 2) иначе — первая картинка товара
      if (!this.product || !this.product.images || !this.product.images.length)
        return null;
      const first = this.product.images[0] || {};
      const file = first.image_original || first.image_preview;
      return file ? this.cdnBase + file : null;
    },
    // Цена: приоритет — выбранный вариант, иначе product.price
    displayPrice() {
      const vp = this.normalizePrice(
        this.selectedVariant ? this.selectedVariant.price : null
      );
      if (vp !== null) return vp;
      return this.normalizePrice(this.product ? this.product.price : null);
    },
  },
  mounted() {
    const id = this.$route.query.id;
    this.fetchProduct(id);
  },
  watch: {
    "$route.query.id"(nextId) {
      this.fetchProduct(nextId);
    },
  },
  methods: {
    async fetchProduct(id) {
      this.product = null;
      this.error = null;
      this.loaded = false;
      this.selectedVariantIndex = 0;

      if (!id) {
        this.error = "Не передан id товара (?id=...)";
        return;
      }

      try {
        this.loading = true;
        const { data } = await this.$http.get(
          `https://api.glamourboutique.uz/v1/product/${id}`
        );
        if (data && data.data) {
          this.product = data.data;

          // выставим дефолтный вариант, если есть
          if (this.variants.length) {
            this.selectVariant(0);
          } else {
            // если вариантов нет — заполним форму общими данными
            this.form.size = "";
            this.form.sku = "";
            this.form.price = this.normalizePrice(this.product.price);
          }
        } else {
          this.error = "Пустой ответ API";
        }
        this.loaded = true;
      } catch (e) {
        this.error = "Ошибка загрузки товара";
      } finally {
        this.loading = false;
      }
    },
    selectVariant(i) {
      this.selectedVariantIndex = i;
      const v = this.variants[i];
      if (!v) return;
      this.form.size = v.size || "";
      this.form.sku = v.sku || "";
      this.form.price = this.normalizePrice(v.price);
    },
    normalizePrice(p) {
      if (p === null || p === undefined || p === "") return null;
      // API иногда отдаёт строку "55083000.00" — приведём к числу
      const num =
        typeof p === "number" ? p : parseFloat(String(p).replace(/\s/g, ""));
      return Number.isFinite(num) ? Math.round(num) : null;
    },
    formatPrice(p) {
      const n = this.normalizePrice(p);
      if (n === null) return "";
      // UZS без знака, группировка пробелами
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    async submitForm() {
      const p = this.product || {};
      this.loading = true;

      //this.form.phone validation uzbek uzbek phone 998 d9
      const phone = String(this.form.phone || "").trim();
      const phoneRegex = /^(998)\d{9}$/;
      if (!phoneRegex.test(phone)) {
        alert("Введите корректный номер телефона в формате 998901234567");
        this.loading = false;
        return;
      }

      //if form.name is empty
      const name = String(this.form.name || "").trim();
      if (!name) {
        alert("Введите ваше имя");
        this.loading = false;
        return;
      }

      const payload = {
        product_id: p.id ? p.id : null,
        product_name: p.name ? p.name : "",
        size: this.form.size,
        sku: this.form.sku,
        price: this.form.price, // число в UZS
        phone: this.form.phone,
        name: this.form.name,
        // comment: this.form.comment,
        url: p.url
          ? p.url
          : typeof window !== "undefined"
          ? window.location.href
          : "",
      };

      console.log(payload);
      //send axsios post request
      //https://api-server.glamourboutique.uz/api/store-order
      try {
        await this.$http.post(
          "https://api-server.glamourboutique.uz/api/store-order",
          payload
        );

        this.sent = true; // форма отправлена
      } catch (e) {
        //alert laravel message
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          "Ошибка отправки формы";
        alert(msg);
        this.loading = false;
        return;
      }

      console.log("Форма отправлена:", payload);
      alert("Спасибо за заказ! Мы свяжемся с вами.");

      this.loading = false;
    },
  },
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  display: block;
  width: 100%;
  place-items: center;
  padding: 24px;
  background: #f6f7f9;
}

.card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  padding: 24px;
  text-align: center;
}

.image-wrap {
  width: 80%;
  aspect-ratio: 3 / 4;
  margin: auto;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #222;
}

.brand {
  font-size: 14px;
  color: #6a6f76;
  margin: 0 0 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.price {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #111;
}

.desc {
  font-size: 16px;
  line-height: 1.6;
  color: #2e3238;
  margin: 0 0 8px;
}

.detail {
  font-size: 14px;
  color: #4a4f55;
  margin: 0 0 16px;
}

.cats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 10px;
}

.chip {
  padding: 4px 9px;
  border-radius: 9px;
  background: #f0f2f5;
  font-size: 12px;
  color: #424850;
}

/* Варианты */
.variants {
  margin: 16px 0 8px;
  text-align: left;
}

.variants-title {
  font-size: 14px;
  color: #6a6f76;
  margin-bottom: 8px;
  text-align: center;
}

.variants-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.variant-chip {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 10px;
  padding: 5px 6px;
  cursor: pointer;
  min-width: 92px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  transition: all 0.15s ease;
}

.variant-chip:hover {
  border-color: #111;
}

.variant-chip.active {
  border-color: #111;
  background: #111;
  color: #fff;
}

.variant-size {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.variant-price {
  font-size: 12px;
  opacity: 0.8;
}

.order-form {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-form input,
.order-form textarea {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
  resize: none;
}

.order-form button {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #222;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 15px;
}

.order-form button:hover {
  background: #444;
}

.state {
  color: #6a6f76;
  font-size: 16px;
}

.state.error {
  color: #cc2b2b;
}

@media (max-width: 480px) {
  .card {
    padding: 16px;
    border-radius: 12px;
  }
  .title {
    font-size: 24px;
  }
}

.logo {
  text-align: center;
  margin: auto;
  width: 100px;
  margin-bottom: 16px;
}

.loading {
  opacity: 0.6;
  pointer-events: none;
}
</style>
